import { useState } from "react";
import { Check, Copy, Mail, MessageSquare, PhoneCall } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { useListenedHandled } from "@/hooks/conversation/useListenedHandled";
import { isListenedCall } from "@/lib/conversation/listenedCall";
import type { Conversation, ConversationAttention } from "@/types/conversation/conversation";
import type { ListenedCallFollowUp } from "@/types/conversation/listened";
import { ListenedAssignee, ListenedHandledBar, ListenedResources, TeachGenieNote } from "./ListenedConversationParts";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

/** A business fact the genie left for the team to fill in, e.g. [confirm appointment time]. */
const PLACEHOLDER_RE = /\[[^\]\n]{2,80}\]/;

const ATTENTION_STYLES: Record<ConversationAttention, { label: string; className: string }> = {
  urgent: { label: "Urgent", className: "bg-genie-secondary text-white" },
  high: { label: "High priority", className: "bg-genie-secondary/15 text-genie-secondary" },
  normal: { label: "Needs a follow-up", className: "bg-genie-accent/15 text-genie-accent-dark" },
};

const pickString = (value: unknown): string | null =>
  typeof value === "string" && value.trim() ? value.trim() : null;

interface ListenedCallFollowUpCardProps {
  conversation: Conversation;
}

/**
 * A phone call the Listener genie heard that needs someone: the follow-up it
 * suggests from the genie's knowledge (a text or email), points for a call
 * back, and the genie's own resources that help. The team sends it from their
 * own phone or mail app and marks the call handled.
 */
export function ListenedCallFollowUpCard({ conversation }: ListenedCallFollowUpCardProps) {
  const followUp = conversation.details?.analysis_result?.call as ListenedCallFollowUp | undefined;
  const [message, setMessage] = useState(followUp?.suggested_message ?? "");
  const [copied, setCopied] = useState(false);
  const [openedElsewhere, setOpenedElsewhere] = useState(false);
  const { handled, savingHandled, updateHandled, markHandledOnTrust } = useListenedHandled(conversation);

  if (!isListenedCall(conversation) || (!followUp && !conversation.attention)) return null;

  const metadata = (conversation.metadata ?? {}) as Record<string, unknown>;
  const leadInfo = (conversation.details?.lead_info ?? {}) as Record<string, unknown>;
  const callerNumber = followUp?.caller ?? pickString(metadata.from);
  const callerName = pickString(leadInfo.full_name) || pickString(leadInfo.name);
  const callerEmail = pickString(leadInfo.email);
  const caller = callerName || callerNumber || "the caller";
  const isVoicemail = metadata.kind === "voicemail";
  const attention = conversation.attention ? ATTENTION_STYLES[conversation.attention] : null;
  const topic = conversation.details?.topics?.[0];
  const body = message.trim();
  const hasPlaceholders = PLACEHOLDER_RE.test(message);
  const subject = followUp?.subject || "Following up on your call";

  const openElsewhere = async () => {
    setOpenedElsewhere(true);
    await markHandledOnTrust("No more reminders for this call.");
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setCopied(true);
      toast.success("Follow-up copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Couldn't copy. Select and copy manually.");
    }
  };

  return (
    <div className="p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] shadow-sm">
      <div className="rounded-[14px] bg-white overflow-hidden">
        <div
          className="px-5 md:px-6 py-4 border-b border-[#4E9CFF]/10"
          style={{ background: "linear-gradient(135deg, rgba(78,156,255,0.06) 0%, rgba(255,77,184,0.06) 100%)" }}
        >
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <GenieIcon
                name="phone"
                size={26}
                color="#4E9CFF"
                secondaryColor="#FF4DB8"
                gradientEnabled
                label={isVoicemail ? "Voicemail" : "Phone call"}
              />
              <div className="min-w-0">
                <h3 className="text-base font-bold text-gray-900 truncate">
                  {isVoicemail ? "Voicemail" : "Call"} from {caller}
                </h3>
                {callerName && callerNumber && <p className="text-xs text-gray-500 truncate">{callerNumber}</p>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {attention && (
                <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${attention.className}`}>
                  {attention.label}
                </span>
              )}
              {topic && (
                <span className="rounded-full px-2.5 py-1 text-xs font-medium bg-gray-100 text-gray-600 capitalize">
                  {topic}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="px-5 md:px-6 py-5 space-y-5">
          <ListenedAssignee conversation={conversation} />

          <div className="space-y-2">
            <p className="text-sm font-semibold text-gray-900">
              {followUp?.suggested_message ? "Suggested follow-up" : "Your follow-up"}
            </p>
            {!followUp?.suggested_message && (
              <p className="text-sm text-gray-600">
                Your genie couldn't draft a follow-up this time. Write it here, then send it from your phone or mail app.
              </p>
            )}
            {hasPlaceholders && (
              <p className="text-sm text-gray-700 rounded-lg bg-genie-accent/10 px-3 py-2">
                Fill in the parts in [square brackets] before sending. Your genie didn't know them.
              </p>
            )}
            <textarea
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder={`Write your follow-up to ${caller}…`}
              rows={Math.min(10, Math.max(4, message.split("\n").length + 1))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2.5 text-base sm:text-sm text-gray-800 leading-relaxed outline-none focus:border-genie-primary"
            />
            {/* TODO: send the text from the genie's own number (Twilio), threaded like
                email's Send, once SMS opt-out and consent handling are in place. */}
            <div className="flex flex-col sm:flex-row sm:flex-wrap gap-2">
              {callerNumber && (
                <Button
                  type="button"
                  asChild
                  className="min-h-[44px] sm:min-h-0 bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8] text-white border-0 hover:brightness-105"
                >
                  <a href={`sms:${callerNumber}?body=${encodeURIComponent(body)}`} onClick={openElsewhere}>
                    <MessageSquare className="w-4 h-4 mr-1.5" />
                    Text {callerName || callerNumber}
                  </a>
                </Button>
              )}
              {callerEmail && (
                <Button type="button" variant="outline" asChild className="min-h-[44px] sm:min-h-0">
                  <a
                    href={`mailto:${encodeURIComponent(callerEmail)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`}
                    onClick={openElsewhere}
                  >
                    <Mail className="w-4 h-4 mr-1.5" />
                    Email {callerEmail}
                  </a>
                </Button>
              )}
              <Button
                type="button"
                variant="outline"
                onClick={handleCopy}
                disabled={!body}
                className="min-h-[44px] sm:min-h-0"
              >
                {copied ? <Check className="w-4 h-4 mr-1.5" /> : <Copy className="w-4 h-4 mr-1.5" />}
                Copy
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Text and Email open your own phone or mail app with the follow-up filled in, so it comes from you.
            </p>
          </div>

          {followUp && followUp.call_back_points.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <p className="text-sm font-semibold text-gray-900">If you call back</p>
                {callerNumber && (
                  <Button type="button" variant="outline" size="sm" asChild className="min-h-[44px] sm:min-h-0">
                    <a href={`tel:${callerNumber}`} onClick={openElsewhere}>
                      <PhoneCall className="w-4 h-4 mr-1.5" />
                      Call {callerName || callerNumber}
                    </a>
                  </Button>
                )}
              </div>
              <ul className="space-y-1.5">
                {followUp.call_back_points.map((point) => (
                  <li key={point} className="flex items-start gap-2 text-sm text-gray-700">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-genie-primary" aria-hidden />
                    {point}
                  </li>
                ))}
              </ul>
            </div>
          )}

          <ListenedHandledBar
            handled={handled}
            saving={savingHandled}
            onToggle={() => updateHandled(!handled)}
            nudged={openedElsewhere}
            handledMessage="Handled. No more reminders for this call."
            openMessage={
              openedElsewhere
                ? "Followed up? Mark it handled so your genie stops reminding you."
                : "Followed up some other way? Mark it handled."
            }
          />

          {followUp?.gap_note && <TeachGenieNote gapNote={followUp.gap_note} agentId={conversation.agent_id} subject="calls" />}

          {followUp && <ListenedResources resources={followUp.resources} />}
        </div>
      </div>
    </div>
  );
}
